'use client';

import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';
import styles from './BusinessCenters.module.css';

const centers = [
    {
        id: 'coworking',
        title: "Coworking Spaces",
        desc: "Flexible desks in a high-tech environment. Ideal for freelancers and startups.",
        image: "/images/coworking.webp",
        feature: "Ejari Compliant"
    },
    {
        id: 'private',
        title: "Private Offices",
        desc: "Fully furnished executive suites with city views. Perfect for established teams.",
        image: "/images/private-office.webp",
        feature: "4-Visa Eligibility"
    },
    {
        id: 'meeting',
        title: "Meeting Rooms",
        feature: "On-Demand Booking",
        desc: "Premium boardrooms for client meetings, audits, and bank inspections.",
        image: "/images/meeting-room.webp"
    }
];

export default function BusinessCenters() {
    const [activeTab, setActiveTab] = useState(0);

    return (
        <section className={styles.section}>
            <div className={`container ${styles.contentWrapper}`}>

                {/* Left: Content & Tabs */}
                <div className={styles.textContent}>
                    <motion.span
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        className={styles.label}
                    >
                        Physical Presence
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className={styles.title}
                    >
                        Real Offices in <br /> <span style={{ color: 'var(--rb-green)' }}>Ras Al Khaimah</span>
                    </motion.h2>
                    <p className={styles.description}>
                        Unlike virtual-only providers, RotanaBiz owns and operates 20,000 sq.ft. of premium office space. Your business gets a verifiable physical address from Day 1.
                    </p>

                    <div className={styles.tabs}>
                        {centers.map((center, index) => (
                            <CenterTab
                                key={center.id}
                                center={center}
                                isActive={activeTab === index}
                                onClick={() => setActiveTab(index)}
                            />
                        ))}
                    </div>

                    <div className={styles.btnGroup}>
                        <Link href="/contact" className={styles.primaryBtn}>Book a Visit</Link>
                        <Link href="/services/office" className={styles.secondaryBtn}>Request Package</Link>
                    </div>
                </div>

                {/* Right: Dynamic Image */}
                <div className={styles.imageSide}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, scale: 1.05 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                            className={styles.imageContainer}
                            style={{
                                background: `url(${centers[activeTab].image}) center/cover no-repeat`
                            }}
                        />
                    </AnimatePresence>

                    <div className={styles.overlayInfo}>
                        <div className={styles.badge}>
                            <CheckCircle2 size={16} />
                            {centers[activeTab].feature}
                        </div>
                        <div style={{ color: '#fff', fontSize: '0.9rem', opacity: 0.8 }}>
                            Rotana Business Center, RAK
                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
}

function CenterTab({ center, isActive, onClick }: any) {
    const tabRef = useRef<HTMLDivElement>(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!tabRef.current) return;
        const rect = tabRef.current.getBoundingClientRect();
        setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <div
            ref={tabRef}
            className={`${styles.tab} ${isActive ? styles.tabActive : ''} glass-card-hover`}
            onClick={onClick}
            onMouseMove={handleMouseMove}
        >
            <div
                className={styles.cardGlow}
                style={{ left: mousePos.x, top: mousePos.y }}
            />
            <div className={styles.tabContent}>
                <h4>{center.title}</h4>
                <p>{center.desc}</p>
            </div>
            <ArrowRight className={styles.tabArrow} size={20} />
        </div>
    );
}
